import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";

const fmtUSD = (n: number) =>
  !isFinite(n) ? "—"
  : Math.abs(n) >= 1_000_000 ? `${n < 0 ? "-" : ""}$${(Math.abs(n) / 1_000_000).toFixed(2)}M`
  : Math.abs(n) >= 1_000     ? `${n < 0 ? "-" : ""}$${(Math.abs(n) / 1_000).toFixed(0)}k`
  :                            `${n < 0 ? "-" : ""}$${Math.round(Math.abs(n)).toLocaleString()}`;
const fmtMo = (m: number) => {
  if (!isFinite(m) || m <= 0) return "—";
  return m < 24 ? `${m.toFixed(1)} mo` : `${(m / 12).toFixed(1)} yr`;
};
const fmtNum = (n: number) => Math.round(n).toLocaleString();

interface Project {
  id: string; project_code: string; project_name: string;
  county: string; tech_type: string; awarded_bsl_count: number;
}
interface BoltData { bolton_count: number; awarded_points: Array<[number, number]>; features: { features: any[] }; tier_counts?: number[] | null }

interface ScoreCol { passings: number; capex: number; federal: number; privateCapital: number; mrr: number }

function Score({ cols, margin, horizon, showBolt }: { cols: { bead: ScoreCol; bolt: ScoreCol; combined: ScoreCol }; margin: number; horizon: number; showBolt: boolean }) {
  const calc = (c: ScoreCol) => {
    const gm = c.mrr * margin;
    return { gm, payback: gm > 0 ? c.privateCapital / gm : Infinity, cashAtHorizon: gm * horizon - c.privateCapital };
  };
  const b = calc(cols.bead); const bo = calc(cols.bolt); const co = calc(cols.combined);
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
      <thead>
        <tr style={{ background: "#f1f5f9" }}>
          <th style={{ textAlign: "left", padding: "6px 8px" }}></th>
          <th style={{ textAlign: "right", padding: "6px 8px" }}>BEAD</th>
          {showBolt && <th style={{ textAlign: "right", padding: "6px 8px", color: "#b45309" }}>+ Bolt-on</th>}
          {showBolt && <th style={{ textAlign: "right", padding: "6px 8px", color: "#047857" }}>= Combined</th>}
        </tr>
      </thead>
      <tbody>
        <tr><td style={{ padding: "4px 8px" }}>Passings</td>
          <td style={{ padding: "4px 8px", textAlign: "right", fontWeight: 600 }}>{fmtNum(cols.bead.passings)}</td>
          {showBolt && <td style={{ padding: "4px 8px", textAlign: "right" }}>+{fmtNum(cols.bolt.passings)}</td>}
          {showBolt && <td style={{ padding: "4px 8px", textAlign: "right", fontWeight: 700, color: "#047857" }}>{fmtNum(cols.combined.passings)}</td>}
        </tr>
        <tr><td style={{ padding: "4px 8px" }}>Capex</td>
          <td style={{ padding: "4px 8px", textAlign: "right", fontWeight: 600 }}>{fmtUSD(cols.bead.capex)}</td>
          {showBolt && <td style={{ padding: "4px 8px", textAlign: "right" }}>+{fmtUSD(cols.bolt.capex)}</td>}
          {showBolt && <td style={{ padding: "4px 8px", textAlign: "right", fontWeight: 700, color: "#047857" }}>{fmtUSD(cols.combined.capex)}</td>}
        </tr>
        <tr><td style={{ padding: "2px 8px 2px 22px", color: "#64748b", fontSize: 11 }}>Federal grant</td>
          <td style={{ padding: "2px 8px", textAlign: "right", color: "#64748b", fontSize: 11 }}>{fmtUSD(cols.bead.federal)}</td>
          {showBolt && <td style={{ padding: "2px 8px", textAlign: "right", color: "#64748b", fontSize: 11 }}>—</td>}
          {showBolt && <td style={{ padding: "2px 8px", textAlign: "right", color: "#64748b", fontSize: 11 }}>{fmtUSD(cols.combined.federal)}</td>}
        </tr>
        <tr><td style={{ padding: "2px 8px 2px 22px", color: "#64748b", fontSize: 11 }}>Private (out-of-pocket)</td>
          <td style={{ padding: "2px 8px", textAlign: "right", fontWeight: 600 }}>{fmtUSD(cols.bead.privateCapital)}</td>
          {showBolt && <td style={{ padding: "2px 8px", textAlign: "right" }}>+{fmtUSD(cols.bolt.privateCapital)}</td>}
          {showBolt && <td style={{ padding: "2px 8px", textAlign: "right", fontWeight: 700, color: "#047857" }}>{fmtUSD(cols.combined.privateCapital)}</td>}
        </tr>
        <tr><td style={{ padding: "4px 8px" }}>MRR (take × ARPU)</td>
          <td style={{ padding: "4px 8px", textAlign: "right", fontWeight: 600 }}>{fmtUSD(cols.bead.mrr)}/mo</td>
          {showBolt && <td style={{ padding: "4px 8px", textAlign: "right" }}>+{fmtUSD(cols.bolt.mrr)}/mo</td>}
          {showBolt && <td style={{ padding: "4px 8px", textAlign: "right", fontWeight: 700, color: "#047857" }}>{fmtUSD(cols.combined.mrr)}/mo</td>}
        </tr>
        <tr><td style={{ padding: "2px 8px 2px 22px", color: "#64748b", fontSize: 11 }}>Margin @ {(margin*100).toFixed(0)}%</td>
          <td style={{ padding: "2px 8px", textAlign: "right", color: "#64748b", fontSize: 11 }}>{fmtUSD(b.gm)}/mo</td>
          {showBolt && <td style={{ padding: "2px 8px", textAlign: "right", color: "#64748b", fontSize: 11 }}>{fmtUSD(bo.gm)}/mo</td>}
          {showBolt && <td style={{ padding: "2px 8px", textAlign: "right", color: "#64748b", fontSize: 11 }}>{fmtUSD(co.gm)}/mo</td>}
        </tr>
        <tr>
          <td style={{ padding: "2px 8px 2px 22px", color: "#64748b", fontSize: 11 }}>Cash @ {horizon} mo</td>
          <td style={{ padding: "2px 8px", textAlign: "right", fontSize: 11, color: b.cashAtHorizon >= 0 ? "#10b981" : "#b45309" }}>{b.cashAtHorizon >= 0 ? "+" : "−"}{fmtUSD(Math.abs(b.cashAtHorizon))}</td>
          {showBolt && <td style={{ padding: "2px 8px", textAlign: "right", fontSize: 11, color: bo.cashAtHorizon >= 0 ? "#10b981" : "#b45309" }}>{bo.cashAtHorizon >= 0 ? "+" : "−"}{fmtUSD(Math.abs(bo.cashAtHorizon))}</td>}
          {showBolt && <td style={{ padding: "2px 8px", textAlign: "right", fontSize: 11, color: co.cashAtHorizon >= 0 ? "#10b981" : "#b45309" }}>{co.cashAtHorizon >= 0 ? "+" : "−"}{fmtUSD(Math.abs(co.cashAtHorizon))}</td>}
        </tr>
        <tr style={{ borderTop: "2px solid #cbd5e1" }}>
          <td style={{ padding: "6px 8px", fontWeight: 600 }}>Payback (private)</td>
          <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 700 }}>{fmtMo(b.payback)}</td>
          {showBolt && <td style={{ padding: "6px 8px", textAlign: "right" }}>{fmtMo(bo.payback)}</td>}
          {showBolt && <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 700, color: "#047857" }}>{fmtMo(co.payback)}</td>}
        </tr>
      </tbody>
    </table>
  );
}

export default function BusinessCaseReportPage() {
  const [params] = useSearchParams();
  const projectIds = (params.get("projects") || "").split(",").filter(Boolean);
  const arpu = Number(params.get("arpu") || 75);
  const takeRate = Number(params.get("takeRate") || 0.40);
  const margin = Number(params.get("margin") || 0.60);
  const horizon = Number(params.get("horizon") || 60);
  const boltOnEnabled = params.get("boltOn") === "1";
  const bufferFt = Number(params.get("bufferFt") || 1500);
  const tiers = (params.get("tiers") || "200:100,500:350,750:600,1000:900,1500:1300,inf:1800")
    .split(",").map((t) => {
      const [ub, rate] = t.split(":");
      return { ub: ub === "inf" ? Infinity : Number(ub), rate: Number(rate),
               label: ub === "inf" ? "1500+ ft" : `≤${ub} ft` };
    });

  const [projects, setProjects] = useState<Project[]>([]);
  const [budgets, setBudgets] = useState<Record<string, { total: string; federal: string; match: string }>>({});
  const [boltOns, setBoltOns] = useState<Record<string, BoltData>>({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Project Economics Report";
    if (projectIds.length === 0) { setErr("No projects in URL"); setLoading(false); return; }
    (async () => {
      try {
        const allProjects = await api.get("/bead/projects") as Project[];
        setProjects(allProjects.filter((p) => projectIds.includes(p.id)));
        const budgetEntries = await Promise.all(projectIds.map(async (id) => {
          const b = await api.get(`/bead/projects/${id}/budget`) as { totals: any };
          return [id, b.totals] as const;
        }));
        setBudgets(Object.fromEntries(budgetEntries));
        if (boltOnEnabled) {
          const boltEntries = await Promise.all(projectIds.map(async (id) => {
            const r = await api.get(`/bead/projects/${id}/bolt-on?buffer_ft=${bufferFt}`) as BoltData;
            return [id, r] as const;
          }));
          setBoltOns(Object.fromEntries(boltEntries));
        }
      } catch (e: any) {
        setErr(e?.message ?? String(e));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div style={{ padding: 32, fontFamily: "system-ui" }}>Loading report…</div>;
  if (err) return <div style={{ padding: 32, fontFamily: "system-ui", color: "#b91c1c" }}>Error: {err}</div>;

  // Compute per-project + portfolio scoreboards
  const FT_PER_DEG_LAT = 364320;
  const businessCases = projects.map((p) => {
    const b = budgets[p.id] ?? { total: 0, federal: 0, match: 0 };
    const totalCapex = Number(b.total ?? 0);
    const federalShare = Number(b.federal ?? 0);
    const matchShare = Number(b.match ?? 0);
    const awardedPassings = p.awarded_bsl_count;
    const mrr = awardedPassings * takeRate * arpu;
    const boltData = boltOns[p.id];
    const boltOnCount = boltData?.bolton_count ?? 0;
    const boltOnMrr = boltOnCount * takeRate * arpu;
    const awardedPts = boltData?.awarded_points ?? [];
    const boltFeats: any[] = boltData?.features?.features ?? [];
    // For TX projects the backend pre-buckets BSLs into tiers (no per-feature
    // lat/lng available). Use those preset counts directly when present;
    // otherwise fall back to the per-feature distance loop (WY/CO/NE).
    const presetTiers: number[] | null = Array.isArray(boltData?.tier_counts) ? boltData.tier_counts : null;
    let tierCounts: number[];
    if (presetTiers && presetTiers.length === tiers.length) {
      tierCounts = presetTiers.slice();
    } else {
      tierCounts = new Array(tiers.length).fill(0);
      for (const f of boltFeats) {
        const c = f.geometry?.coordinates;
        if (!Array.isArray(c) || awardedPts.length === 0) continue;
        const cosLat = Math.cos((c[1] as number) * Math.PI / 180);
        let minD2 = Infinity;
        for (const ap of awardedPts) {
          const dx = ((c[0] as number) - ap[0]) * cosLat;
          const dy = (c[1] as number) - ap[1];
          const d2 = dx * dx + dy * dy;
          if (d2 < minD2) minD2 = d2;
        }
        const dFt = Math.sqrt(minD2) * FT_PER_DEG_LAT;
        for (let i = 0; i < tiers.length; i++) {
          if (dFt <= tiers[i].ub) { tierCounts[i]++; break; }
        }
      }
    }
    const boltOnCapex = tiers.reduce((acc, t, i) => acc + tierCounts[i] * t.rate, 0);
    return {
      project: p, totalCapex, federalShare, matchShare,
      awardedPassings, mrr, boltOnCount, boltOnMrr, boltOnCapex, tierCounts,
    };
  });

  const port = businessCases.reduce((acc, bc) => ({
    passings: acc.passings + bc.awardedPassings,
    capex: acc.capex + bc.totalCapex,
    federal: acc.federal + bc.federalShare,
    match: acc.match + bc.matchShare,
    mrr: acc.mrr + bc.mrr,
    bolt_passings: acc.bolt_passings + (boltOnEnabled ? bc.boltOnCount : 0),
    bolt_capex: acc.bolt_capex + (boltOnEnabled ? bc.boltOnCapex : 0),
    bolt_mrr: acc.bolt_mrr + (boltOnEnabled ? bc.boltOnMrr : 0),
  }), { passings: 0, capex: 0, federal: 0, match: 0, mrr: 0, bolt_passings: 0, bolt_capex: 0, bolt_mrr: 0 });

  const portCols = {
    bead: { passings: port.passings, capex: port.capex, federal: port.federal, privateCapital: port.match, mrr: port.mrr },
    bolt: { passings: port.bolt_passings, capex: port.bolt_capex, federal: 0, privateCapital: port.bolt_capex, mrr: port.bolt_mrr },
    combined: { passings: port.passings + port.bolt_passings, capex: port.capex + port.bolt_capex,
                federal: port.federal, privateCapital: port.match + port.bolt_capex,
                mrr: port.mrr + port.bolt_mrr },
  };
  const showBolt = boltOnEnabled && port.bolt_passings > 0;

  return (
    <div style={{ padding: "32px 40px", maxWidth: 1100, margin: "0 auto", fontFamily: "system-ui, -apple-system, sans-serif", color: "#0f172a", background: "#fff" }}>
      <style>{`@media print { body { background: #fff !important; } .no-print { display: none !important; } table { page-break-inside: auto; } tr { page-break-inside: avoid; } h2 { page-break-after: avoid; } }`}</style>

      <div className="no-print" style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
        <button onClick={() => window.print()} style={{ background: "#0f172a", color: "#fff", border: 0, padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>🖨️ Print / Save PDF</button>
        <button onClick={() => window.close()} style={{ background: "#fff", color: "#0f172a", border: "1px solid #cbd5e1", padding: "8px 16px", borderRadius: 6, cursor: "pointer" }}>Close</button>
      </div>

      <h1 style={{ margin: 0, fontSize: 26 }}>Project Economics Report</h1>
      <div style={{ color: "#64748b", fontSize: 13, marginTop: 4 }}>
        {new Date().toLocaleString()} · {businessCases.length} project{businessCases.length === 1 ? "" : "s"}
      </div>

      {/* Assumptions */}
      <h2 style={{ marginTop: 28, fontSize: 14, textTransform: "uppercase", letterSpacing: ".06em", color: "#334155", borderBottom: "1px solid #cbd5e1", paddingBottom: 6 }}>Assumptions</h2>
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8, fontSize: 12 }}>
        <tbody>
          <tr><td style={{ padding: "4px 8px", color: "#64748b", width: "40%" }}>ARPU</td><td style={{ padding: "4px 8px", fontWeight: 600 }}>${arpu}/mo per subscriber</td></tr>
          <tr><td style={{ padding: "4px 8px", color: "#64748b" }}>Take rate</td><td style={{ padding: "4px 8px", fontWeight: 600 }}>{(takeRate * 100).toFixed(0)}%</td></tr>
          <tr><td style={{ padding: "4px 8px", color: "#64748b" }}>Gross margin</td><td style={{ padding: "4px 8px", fontWeight: 600 }}>{(margin * 100).toFixed(0)}%</td></tr>
          <tr><td style={{ padding: "4px 8px", color: "#64748b" }}>Horizon</td><td style={{ padding: "4px 8px", fontWeight: 600 }}>{horizon} months</td></tr>
          <tr><td style={{ padding: "4px 8px", color: "#64748b" }}>Bolt-on (non-BEAD nearby passings)</td><td style={{ padding: "4px 8px", fontWeight: 600 }}>{boltOnEnabled ? `Enabled · ${bufferFt} ft buffer` : "Disabled"}</td></tr>
          {boltOnEnabled && (
            <tr><td style={{ padding: "4px 8px", color: "#64748b", verticalAlign: "top" }}>Per-passing $ by distance to BSL</td>
              <td style={{ padding: "4px 8px" }}>
                <table style={{ borderCollapse: "collapse", fontSize: 11 }}>
                  <tbody>
                    {tiers.map((t, i) => (
                      <tr key={i}><td style={{ padding: "1px 8px 1px 0", color: "#64748b" }}>{t.label}</td><td style={{ padding: "1px 0", fontWeight: 600 }}>${t.rate}</td></tr>
                    ))}
                  </tbody>
                </table>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Portfolio */}
      <h2 style={{ marginTop: 28, fontSize: 14, textTransform: "uppercase", letterSpacing: ".06em", color: "#334155", borderBottom: "1px solid #cbd5e1", paddingBottom: 6 }}>Portfolio Summary</h2>
      <Score cols={portCols} margin={margin} horizon={horizon} showBolt={showBolt} />

      {/* Per project */}
      <h2 style={{ marginTop: 28, fontSize: 14, textTransform: "uppercase", letterSpacing: ".06em", color: "#334155", borderBottom: "1px solid #cbd5e1", paddingBottom: 6 }}>Per Project ({businessCases.length})</h2>
      {businessCases.map((bc) => {
        const cols = {
          bead: { passings: bc.awardedPassings, capex: bc.totalCapex, federal: bc.federalShare, privateCapital: bc.matchShare, mrr: bc.mrr },
          bolt: { passings: bc.boltOnCount, capex: bc.boltOnCapex, federal: 0, privateCapital: bc.boltOnCapex, mrr: bc.boltOnMrr },
          combined: { passings: bc.awardedPassings + (boltOnEnabled ? bc.boltOnCount : 0),
                      capex: bc.totalCapex + (boltOnEnabled ? bc.boltOnCapex : 0),
                      federal: bc.federalShare,
                      privateCapital: bc.matchShare + (boltOnEnabled ? bc.boltOnCapex : 0),
                      mrr: bc.mrr + (boltOnEnabled ? bc.boltOnMrr : 0) },
        };
        const showB = boltOnEnabled && bc.boltOnCount > 0;
        const combinedGm = cols.combined.mrr * margin;
        const payback = combinedGm > 0 ? cols.combined.privateCapital / combinedGm : Infinity;
        const verdict = payback <= 120 ? "BUILD" : payback <= 180 ? "MARGINAL" : "WALK AWAY";
        const verdictBg = verdict === "BUILD" ? "#dcfce7" : verdict === "MARGINAL" ? "#fef3c7" : "#fee2e2";
        const verdictFg = verdict === "BUILD" ? "#047857" : verdict === "MARGINAL" ? "#92400e" : "#b91c1c";
        return (
          <div key={bc.project.id} style={{ marginTop: 18, paddingTop: 12, borderTop: "1px dashed #e2e8f0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{bc.project.project_code}</div>
                <div style={{ fontSize: 11, color: "#64748b" }}>{bc.project.project_name} · {bc.project.county} County · {bc.project.tech_type}</div>
              </div>
              <span style={{ padding: "3px 12px", borderRadius: 4, fontSize: 12, fontWeight: 700, background: verdictBg, color: verdictFg }}>{verdict}</span>
            </div>
            <Score cols={cols} margin={margin} horizon={horizon} showBolt={showB} />
            {showB && bc.tierCounts.some((n: number) => n > 0) && (
              <table style={{ width: "100%", marginTop: 6, fontSize: 11, background: "#fffbeb", borderCollapse: "collapse" }}>
                <tbody>
                  <tr><td colSpan={3} style={{ padding: "4px 8px", fontWeight: 600, color: "#92400e" }}>Bolt-on tier breakdown</td></tr>
                  {bc.tierCounts.map((n: number, i: number) => n > 0 && (
                    <tr key={i}>
                      <td style={{ padding: "2px 8px", color: "#92400e" }}>{tiers[i].label}</td>
                      <td style={{ padding: "2px 8px", color: "#92400e", textAlign: "right" }}>{fmtNum(n)} × ${tiers[i].rate}</td>
                      <td style={{ padding: "2px 8px", color: "#92400e", textAlign: "right", fontWeight: 600 }}>{fmtUSD(n * tiers[i].rate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}

      <div style={{ marginTop: 32, paddingTop: 12, borderTop: "1px solid #cbd5e1", color: "#94a3b8", fontSize: 10, lineHeight: 1.5 }}>
        Generated by FiberForge / Milestone Build · BEAD = federal grant + 25% match (private capital).
        Bolt-on = non-BEAD passings within the buffer of awarded BSLs (ride-along homes the BEAD-funded fiber physically passes).
        Payback computed against private capital out-of-pocket only — federal grant excluded from numerator.
      </div>
    </div>
  );
}
